import { Box, Button, Divider, Popover, Typography } from "@mui/material";
import { Download } from "lucide-react";
import { t } from "i18next";

export type MismatchFilter = { label: string; aiLabel: string } | "all" | null;

type Row = { label?: unknown; AILabel?: string; path?: string; score?: number };

// label → AILabel 불일치 쌍별 개수 (많은 순)
export const getMismatchPairs = (items: Row[]) => {
  const counts = new Map<string, { label: string; aiLabel: string; count: number }>();
  items.forEach((r) => {
    if (!r.label || !r.AILabel || r.label === r.AILabel) return;
    const key = `${r.label}->${r.AILabel}`;
    const cur = counts.get(key) ?? { label: String(r.label), aiLabel: r.AILabel, count: 0 };
    cur.count += 1;
    counts.set(key, cur);
  });
  return Array.from(counts.values()).sort((a, b) => b.count - a.count);
};

export const matchesMismatch = (f: MismatchFilter, r: Row) =>
  !!r.label &&
  !!r.AILabel &&
  r.label !== r.AILabel &&
  (f === "all" || (f !== null && r.label === f.label && r.AILabel === f.aiLabel));

export type ThresholdJump = { rank: number; path?: string } | null;

// threshold와 score가 가까운 순으로 순회: 직전 점프한 행이 아직 선택돼 있으면 다음으로 가까운 행, 아니면 가장 가까운 행
export const nextNearThreshold = (items: Row[], th: number, prev: ThresholdJump, selectedPath?: string) => {
  const dist = (i: number) => Math.abs((items[i].score ?? Infinity) - th);
  const order = items.map((_, i) => i).sort((a, b) => dist(a) - dist(b));
  const rank = prev && prev.path === selectedPath ? (prev.rank + 1) % order.length : 0;
  return { index: order[rank], jump: { rank, path: items[order[rank]].path } };
};

export const exportCsv = (items: Row[], filename: string) => {
  const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [
    "no,path,label,AILabel,score",
    ...items.map((r, i) => [i + 1, r.path, r.label, r.AILabel, r.score].map(esc).join(",")),
  ];
  // BOM: 엑셀 한글 깨짐 방지
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

interface Props {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  mismatchPairs: { label: string; aiLabel: string; count: number }[];
  filter: MismatchFilter;
  onFilterChange: (f: MismatchFilter) => void;
  // 다중 선택이 없는 테이블(검증 리스트)은 생략 → 일괄 라벨링 섹션 숨김
  selectedCount?: number;
  onClearSelection?: () => void;
  visibleCount: number;
  onExportCsv: () => void;
}

const isSamePair = (f: MismatchFilter, p: { label: string; aiLabel: string }) =>
  f !== null && f !== "all" && f.label === p.label && f.aiLabel === p.aiLabel;

export default function TableToolsPanel({
  anchorEl,
  onClose,
  mismatchPairs,
  filter,
  onFilterChange,
  selectedCount,
  onClearSelection,
  visibleCount,
  onExportCsv,
}: Props) {
  const total = mismatchPairs.reduce((sum, p) => sum + p.count, 0);
  const rowSx = (active: boolean) => ({
    display: "flex",
    justifyContent: "space-between",
    px: 1,
    py: 0.5,
    borderRadius: 1,
    fontSize: "12px",
    cursor: "pointer",
    bgcolor: active ? "var(--selected-row-bg)" : "transparent",
    "&:hover": { bgcolor: "action.hover" },
  });

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{ paper: { sx: { mt: 0.5, p: 1.5, width: 240, borderRadius: 2 } } }}
    >
      {/* 불일치(과검/미검) 현황 + 필터 */}

      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{t("MismatchTitle")} (label → AI)</Typography>

      <Typography variant="caption" fontWeight={600} color="text.secondary"></Typography>
      <Box sx={{ mt: 0.5 }}>
        <Box sx={rowSx(filter === null)} onClick={() => onFilterChange(null)}>
          <span>{t("ShowAll")}</span>
        </Box>
        <Box sx={rowSx(filter === "all")} onClick={() => onFilterChange("all")}>
          <span>{t("MismatchOnly")}</span>
          <b>{total}</b>
        </Box>
        {mismatchPairs.map((p) => (
          <Box
            key={`${p.label}->${p.aiLabel}`}
            sx={{ ...rowSx(isSamePair(filter, p)), pl: 2 }}
            onClick={() => {
              onFilterChange({ label: p.label, aiLabel: p.aiLabel });
              onClose();
            }}
          >
            <span>
              {p.label} → {p.aiLabel}
            </span>
            <span>{p.count}</span>
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 1 }} />

      {onClearSelection && (
        <>
          {/* 일괄 라벨링 */}
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}> {t("BulkLabelTitle")}</Typography>
          <Typography sx={{ fontSize: "12px", mt: 0.5, whiteSpace: "pre-line" }}>{t("BulkLabelHelp")}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.5 }}>
            <Typography sx={{ fontSize: "12px" }}>
              {t("SelectedCount")}: <b>{selectedCount}</b>
            </Typography>
            <Button size="small" disabled={!selectedCount} onClick={onClearSelection} sx={{ fontSize: "11px" }}>
              {t("ClearSelection")}
            </Button>
          </Box>

          <Divider sx={{ my: 1 }} />
        </>
      )}

      <Button
        fullWidth
        size="small"
        variant="outlined"
        startIcon={<Download size="14px" />}
        disabled={!visibleCount}
        onClick={onExportCsv}
        sx={{ fontSize: "12px" }}
      >
        CSV ({visibleCount})
      </Button>
    </Popover>
  );
}
